/**
 * PlayDrawer - Drawer latéral pour PlayScreen avec Avatar, ExtraFields, Compétences, Inventaire
 * Design narratif/livre de jeu, cohérent avec le reste de l'application
 */

import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ImageBackground,
  Platform,
} from 'react-native';
import {DrawerContentScrollView, DrawerContentComponentProps} from '@react-navigation/drawer';
import {usePlayer} from '../contexts/PlayerContext';
import {useTheme} from '../contexts/ThemeContext';
import {useNavigation} from '@react-navigation/native';
import {Theme} from '../theme/theme';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {
  faUser,
  faDumbbell,
  faHeart,
  faBolt,
  faStar,
  faShoppingBag,
  faTimes,
  faArrowRight,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import InventoryDialog from './InventoryDialog';
import NotebookTab from './NotebookTab';
import GameDataExtraField from '../models/GameDataExtraField';
import GameDataAttribute from '../models/GameDataAttribute';
import GameRegistry from '../systems/GameRegistry';
import {SolidObject} from '../models/SolidObject';
import {PlayerStepAttribute} from '../models/PlayerStepAttribute';
import {Fonts} from '../config/fonts';

// Import des textures de papier
import paperLight from '../assets/img/paper_light.png';
import paperDark from '../assets/img/paper_dark.png';

interface PlayDrawerProps extends Partial<DrawerContentComponentProps> {
  isDarkMode?: boolean;
  onClose?: () => void;
  asOverlay?: boolean;
  onAvatarPress?: () => void;
  initialTab?: 'stats' | 'skills' | 'inventory' | 'notes';
}

const PlayDrawer: React.FC<PlayDrawerProps> = (props) => {
  const {onClose, asOverlay = false, onAvatarPress, initialTab = 'stats'} = props;
  const {theme, isDarkMode, isReadingMode} = useTheme();
  
  let gameState, currentStep, currentAdventure;
  try {
    const playerContext = usePlayer();
    gameState = playerContext.gameState;
    currentStep = playerContext.currentStep;
    currentAdventure = playerContext.currentAdventure;
  } catch (error) {
    console.warn('[PlayDrawer] PlayerContext not available, rendering empty drawer');
    const styles = createStyles(theme, isReadingMode);
    return (
      <View style={[asOverlay ? styles.overlayContainer : styles.drawer, isReadingMode && styles.containerReadingMode]}>
        {!isReadingMode && (
          <ImageBackground
            source={isDarkMode ? require('../assets/img/paper_dark.png') : require('../assets/img/paper_light.png')}
            style={StyleSheet.absoluteFill}
            imageStyle={styles.backgroundImage}
          />
        )}
        <ScrollView contentContainerStyle={styles.drawerContent}>
          <Text style={styles.emptyText}>Chargement...</Text>
        </ScrollView>
      </View>
    );
  }
  
  const navigation = useNavigation();
  const [showInventory, setShowInventory] = useState(false);
  const [expandedAttributeIndex, setExpandedAttributeIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'skills' | 'inventory' | 'notes'>(initialTab);
  
  // Générer les styles basés sur le thème ET mode lecture
  const styles = React.useMemo(() => createStyles(theme, isReadingMode), [theme, isReadingMode]);
  
  // Mettre à jour l'onglet actif quand initialTab change (pour ouvrir directement sur l'onglet Notes)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const avatarImage =
    gameState?.data.layerAdventurerId ||
    (currentStep?.layerAdventurer?.['type'] === 'picture' ? currentStep.layerAdventurer?.['image'] : null);

  const extraFields: GameDataExtraField[] = gameState?.data.adventureExtraFields || [];

  // Les compétences avec leurs descriptions (sauvegardées dans gameState)
  const attributes: Array<GameDataAttribute & {description?: string}> = [
    ...(gameState?.data.skills || []),
    ...(gameState?.data.butterflyEffects || []),
  ].map((attr) => {
    // Utiliser la description sauvegardée dans le GameDataAttribute, avec fallback sur currentStep si disponible
    return {
      ...attr,
      description: attr.description || currentStep?.attributes?.find(
        (a: PlayerStepAttribute) => a.name === attr.attributeId && a.qualification.toUpperCase() !== 'OBJECT'
      )?.description,
    };
  });

  const inventoryObjects: GameDataAttribute[] = gameState?.data.objects || [];

  const buildImageUri = (imageData?: string): string | null => {
    if (!imageData) return null;
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    return `data:image/png;base64,${imageData}`;
  };

  const getExtraFieldIcon = (typeName: string) => {
    switch (typeName) {
      case 'D6_FORCE':
        return faDumbbell;
      case 'D6_PV':
        return faHeart;
      case 'D6_DAMAGE':
        return faBolt;
      default:
        return faStar;
    }
  };

  const getExtraFieldLabel = (typeName: string): string => {
    const extraFieldType = GameRegistry.getExtraFieldType(typeName);
    switch (typeName) {
      case 'D6_FORCE':
        return 'Force';
      case 'D6_PV':
        return 'Points de vie';
      case 'D6_DAMAGE':
        return 'Dégâts';
      default:
        return extraFieldType?.messageKey || typeName;
    }
  };

  const getExtraFieldColor = (typeName: string): string => {
    switch (typeName) {
      case 'D6_FORCE':
        return '#3B82F6'; // Blue
      case 'D6_PV':
        return '#EF4444'; // Red
      case 'D6_DAMAGE':
        return '#F59E0B'; // Amber
      default:
        return '#8B7355';
    }
  };

  const getObjectDetails = (gameDataAttr: GameDataAttribute): SolidObject | null => {
    if (!currentStep?.objects) return null;
    return currentStep.objects.find((obj) => obj.id === gameDataAttr.attributeId) || null;
  };

  const avatarUri = buildImageUri(avatarImage as string);

  const ScrollComponent = asOverlay ? ScrollView : DrawerContentScrollView;

  return (
    <View style={[asOverlay ? styles.overlayContainer : styles.drawer, isReadingMode && styles.containerReadingMode]}>
      {/* Texture papier : masquée en mode lecture (liseuse) */}
      {!isReadingMode && (
        <ImageBackground
          source={isDarkMode ? paperDark : paperLight}
          style={StyleSheet.absoluteFill}
          imageStyle={styles.backgroundImage}
          resizeMode="stretch"
        />
      )}
      <ScrollComponent {...(asOverlay ? {} : (props as DrawerContentComponentProps))} contentContainerStyle={styles.drawerContent}>
        {/* Bouton Fermer (si overlay) */}
        {asOverlay && onClose && (
          <View style={styles.closeButtonContainer}>
            <TouchableOpacity style={styles.closeButton} onPress={onClose} activeOpacity={0.7}>
              <FontAwesomeIcon icon={faTimes} size={18} color="#8B7355" />
              <Text style={styles.closeButtonText}>Fermer</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Header avec Avatar - Style narratif */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.avatarWrapper}
            onPress={onAvatarPress}
            activeOpacity={0.8}
            disabled={!onAvatarPress}>
            {avatarUri ? (
              <Image source={{uri: avatarUri}} style={styles.avatar} />
            ) : (
              <View style={styles.defaultAvatar}>
                <FontAwesomeIcon icon={faUser} size={36} color={theme.colors.text} />
              </View>
            )}
          </TouchableOpacity>
          <Text style={styles.characterLabel}>
            Votre personnage
          </Text>
        </View>

        {/* Onglets */}
        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'stats' && styles.tabActive]}
            onPress={() => setActiveTab('stats')}
            activeOpacity={0.7}>
            <Text style={[styles.tabText, activeTab === 'stats' && styles.tabTextActive]}>
              Caractéristiques
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'skills' && styles.tabActive]}
            onPress={() => setActiveTab('skills')}
            activeOpacity={0.7}>
            <Text style={[styles.tabText, activeTab === 'skills' && styles.tabTextActive, isDarkMode && styles.tabTextDark]}>
              Compétences
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'inventory' && styles.tabActive]}
            onPress={() => setActiveTab('inventory')}
            activeOpacity={0.7}>
            <View style={styles.tabWithBadge}>
              <Text style={[styles.tabText, activeTab === 'inventory' && styles.tabTextActive]}>
                Inventaire
              </Text>
              {inventoryObjects.length > 0 && (
                <View style={styles.tabBadge}>
                  <Text style={styles.tabBadgeText}>{inventoryObjects.length}</Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'notes' && styles.tabActive]}
            onPress={() => setActiveTab('notes')}
            activeOpacity={0.7}>
            <Text style={[styles.tabText, activeTab === 'notes' && styles.tabTextActive, isDarkMode && styles.tabTextDark]}>
              Notes
            </Text>
          </TouchableOpacity>
        </View>

        {/* Contenu des onglets */}
        {activeTab === 'stats' && (
          <>
            {/* ExtraFields d'aventure (Caractéristiques) - Style carte */}
        {extraFields.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Caractéristiques
            </Text>
            <View style={styles.statsGrid}>
              {extraFields.map((field, index) => {
                const icon = getExtraFieldIcon(field.type);
                const label = getExtraFieldLabel(field.type);
                const color = getExtraFieldColor(field.type);
                const value = field.currentValue ?? field.initValue ?? '0';
                return (
                  <View key={`${field.type}-${index}`} style={styles.statCard}>
                    <View style={styles.statIconContainer}>
                      <FontAwesomeIcon icon={icon} size={20} color={color} />
                    </View>
                    <Text style={styles.statValue}>
                      {value}
                    </Text>
                    <Text style={styles.statLabel}>
                      {label}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

          </>
        )}

        {activeTab === 'skills' && (
          <>
            {/* Compétences - Style liste élégante */}
            <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            Compétences acquises
          </Text>
          {attributes.length === 0 ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyText}>
                Aucune compétence acquise
              </Text>
            </View>
          ) : (
            <View style={styles.skillsList}>
              {attributes.map((attr, index) => {
                const isExpanded = expandedAttributeIndex === index;
                return (
                  <View key={`${attr.attributeId}-${index}`} style={styles.skillCard}>
                    <TouchableOpacity
                      style={styles.skillHeader}
                      onPress={() => setExpandedAttributeIndex(isExpanded ? null : index)}
                      activeOpacity={0.7}>
                      <View style={styles.skillLeft}>
                        <View style={styles.skillIcon}>
                          <FontAwesomeIcon icon={faStar} size={14} color="#8B7355" />
                        </View>
                        <Text style={styles.skillName} numberOfLines={1}>
                          {attr.attributeId}
                          {attr.value && ` (${attr.value})`}
                        </Text>
                      </View>
                      <FontAwesomeIcon 
                        icon={isExpanded ? faChevronDown : faChevronRight} 
                        size={12} 
                        color={isDarkMode ? '#9CA3AF' : '#6B7280'} 
                      />
                    </TouchableOpacity>
                    {isExpanded && attr.description && (
                      <View style={[styles.skillDescription, isDarkMode && styles.skillDescriptionDark]}>
                        <Text style={[styles.skillDescriptionText, isDarkMode && styles.skillDescriptionTextDark]}>
                          {attr.description}
                        </Text>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </View>

          </>
        )}

        {activeTab === 'inventory' && (
          <>
            {/* Inventaire - Style élégant */}
            <View style={styles.section}>
          <View style={styles.inventoryHeader}>
            <View style={styles.inventoryHeaderLeft}>
              <FontAwesomeIcon icon={faShoppingBag} size={18} color="#8B7355" />
              <Text style={[styles.sectionTitle, {marginLeft: 8}]}>
                Inventaire
              </Text>
            </View>
            {inventoryObjects.length > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{inventoryObjects.length}</Text>
              </View>
            )}
          </View>

          {inventoryObjects.length === 0 ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyText}>
                Votre inventaire est vide
              </Text>
            </View>
          ) : (
            <View style={styles.inventoryList}>
              {inventoryObjects.slice(0, 8).map((gameDataAttr, index) => {
                const fullObject = getObjectDetails(gameDataAttr);
                const objectName = fullObject?.name || gameDataAttr.attributeId;
                const objectImageUri = buildImageUri(fullObject?.image);

                return (
                  <TouchableOpacity
                    key={`${gameDataAttr.attributeId}-${index}`}
                    style={styles.inventoryItem}
                    onPress={() => setShowInventory(true)}
                    activeOpacity={0.7}>
                    {objectImageUri ? (
                      <Image source={{uri: objectImageUri}} style={styles.inventoryThumbnail} />
                    ) : (
                      <View style={[styles.inventoryPlaceholder, isDarkMode && styles.inventoryPlaceholderDark]}>
                        <FontAwesomeIcon icon={faShoppingBag} size={12} color={isDarkMode ? '#9CA3AF' : '#9CA3AF'} />
                      </View>
                    )}
                    <Text style={styles.inventoryName} numberOfLines={1}>
                      {objectName}
                    </Text>
                    {inventoryObjects.length > 8 && index === 7 && (
                      <FontAwesomeIcon icon={faArrowRight} size={12} color="#8B7355" style={{marginLeft: 'auto'}} />
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {inventoryObjects.length > 8 && (
            <TouchableOpacity
              style={[styles.seeMoreButton, isDarkMode && styles.seeMoreButtonDark]}
              onPress={() => setShowInventory(true)}
              activeOpacity={0.7}>
              <Text style={[styles.seeMoreText, isDarkMode && styles.seeMoreTextDark]}>
                Voir tous les objets ({inventoryObjects.length})
              </Text>
              <FontAwesomeIcon icon={faArrowRight} size={12} color={theme.colors.textLight} style={{marginLeft: 6}} />
            </TouchableOpacity>
          )}
        </View>
          </>
        )}

        {activeTab === 'notes' && (
          <NotebookTab isDarkMode={isDarkMode} />
        )}
      </ScrollComponent>

      <InventoryDialog visible={showInventory} onClose={() => setShowInventory(false)} />
    </View>
  );
};

// Fonction pour créer les styles basés sur le thème
const createStyles = (theme: Theme, isReadingMode: boolean = false) => StyleSheet.create({
  drawer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  overlayContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  containerReadingMode: {
    backgroundColor: isReadingMode 
      ? (theme.colors.bg === '#EFE8DF' || theme.colors.bg === '#FBF8F2' || theme.colors.bg === '#FFFEF9' ? '#FFFEF9' : '#1C1A17')
      : theme.colors.bg,
  },
  drawerContent: {
    paddingBottom: 32,
  },
  closeButtonContainer: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.objectBorder,
  },
  closeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  closeButtonText: {
    marginLeft: 6,
    fontSize: 14,
    color: theme.colors.textLight,
    fontFamily: Fonts.reading,
  },
  header: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.objectBorder,
    marginBottom: 20,
  },
  avatarWrapper: {
    marginBottom: 12,
  },
  avatar: {
    width: 100,
    height: 100,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 18,
        borderBottomLeftRadius: 14,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
      default: {
        borderRadius: 16,
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 4,
      },
    }),
    borderWidth: 3,
    borderColor: theme.colors.avatarBorder,
  },
  defaultAvatar: {
    width: 100,
    height: 100,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 18,
        borderBottomLeftRadius: 14,
      },
      default: {
        borderRadius: 16,
      },
    }),
    backgroundColor: theme.colors.avatar,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: theme.colors.avatarBorder,
  },
  characterLabel: {
    fontSize: 16,
    fontFamily: Fonts.reading,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    letterSpacing: 0.5,
  },
  section: {
    paddingHorizontal: 20,
    marginBottom: 28,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text,
    fontFamily: Fonts.title,
    marginBottom: 16,
    letterSpacing: 0.3,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: '30%',
    backgroundColor: isReadingMode 
      ? (theme.colors.bg === '#FFFEF9' ? 'rgba(0, 0, 0, 0.025)' : 'rgba(255, 255, 255, 0.04)')
      : theme.colors.cardBg,
    borderRadius: isReadingMode ? theme.radius.md : theme.cardPremium.borderRadius,
    borderWidth: isReadingMode ? 0 : theme.cardPremium.borderWidth,
    borderColor: isReadingMode ? 'transparent' : theme.colors.cardBorder,
    borderTopWidth: isReadingMode ? 0 : 2,
    borderTopColor: isReadingMode ? 'transparent' : theme.colors.cardHighlight,
    ...Platform.select({
      web: {
        boxShadow: isReadingMode ? 'none' : `0 2px 8px rgba(${theme.colors.cardShadow === '#000' ? '0,0,0' : '139,115,85'}, 0.08)`,
        backgroundImage: isReadingMode ? 'none' : `linear-gradient(to bottom, ${theme.colors.cardBgTop}, ${theme.colors.cardBgBottom})`,
      },
      default: {
        shadowColor: isReadingMode ? 'transparent' : theme.colors.cardShadow,
        shadowOffset: {width: 0, height: isReadingMode ? 0 : 2},
        shadowOpacity: 0.1,
        shadowOpacity: isReadingMode ? 0.06 : 0.08,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
    paddingVertical: theme.cardPremium.paddingVertical,
    paddingHorizontal: theme.cardPremium.paddingHorizontal,
    alignItems: 'center',
  },
  statCardDark: {
    // Supprimé car les couleurs sont maintenant dynamiques via theme.colors.cardBg
  },
  statIconContainer: {
    width: isReadingMode ? 0 : theme.cardPremium.iconBadgeSize,
    height: isReadingMode ? 0 : theme.cardPremium.iconBadgeSize,
    borderRadius: theme.cardPremium.iconBadgeRadius,
    backgroundColor: isReadingMode ? 'transparent' : theme.colors.objectBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: isReadingMode ? 0 : theme.spacing.sm,
    opacity: isReadingMode ? 0 : 1,
    overflow: 'hidden',
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.colors.text,
    fontFamily: Fonts.ui,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 11,
    color: theme.colors.textMuted,
    fontFamily: Fonts.ui,
    textAlign: 'center',
  },
  skillsList: {
    gap: 8,
  },
  skillCard: {
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 6,
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
      },
      default: {
        borderRadius: 10,
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.05,
        shadowRadius: 3,
        elevation: 1,
      },
    }),
    borderWidth: 1,
    borderColor: '#E8D5C4',
    overflow: 'hidden',
  },
  skillCardDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  skillHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
  },
  skillLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12,
  },
  skillIcon: {
    marginRight: 10,
  },
  skillName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    fontFamily: Fonts.ui,
  },
  skillDescription: {
    paddingHorizontal: 14,
    paddingBottom: 14,
    paddingTop: 8,
    backgroundColor: theme.colors.objectBg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderLight,
  },
  skillDescriptionText: {
    fontSize: 13,
    fontStyle: 'italic',
    color: theme.colors.textMuted,
    lineHeight: 20,
    fontFamily: Fonts.reading,
  },
  inventoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  inventoryHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inventoryList: {
    gap: 6,
  },
  inventoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: isReadingMode 
      ? (theme.colors.bg === '#FFFEF9' ? 'rgba(0, 0, 0, 0.025)' : 'rgba(255, 255, 255, 0.04)')
      : theme.colors.cardBg,
    borderRadius: isReadingMode ? theme.radius.sm : theme.cardPremium.borderRadius - 3,
    borderWidth: isReadingMode ? 0 : theme.cardPremium.borderWidth,
    borderColor: isReadingMode ? 'transparent' : theme.colors.cardBorder,
    borderTopWidth: isReadingMode ? 0 : 1.5,
    borderTopColor: isReadingMode ? 'transparent' : theme.colors.cardHighlight,
    ...Platform.select({
      web: {
        boxShadow: isReadingMode ? 'none' : `0 1px 4px rgba(${theme.colors.cardShadow === '#000' ? '0,0,0' : '139,115,85'}, 0.06)`,
        backgroundImage: isReadingMode ? 'none' : `linear-gradient(to bottom, ${theme.colors.cardBgTop}, ${theme.colors.cardBgBottom})`,
      },
      default: {
        shadowColor: isReadingMode ? 'transparent' : theme.colors.cardShadow,
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: theme.colors.shadowOpacity,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  inventoryThumbnail: {
    width: 36,
    height: 36,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: theme.colors.border,
    marginRight: 12,
  },
  inventoryPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.surface3,
  },
  inventoryName: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.text,
    fontFamily: Fonts.ui,
  },
  badge: {
    backgroundColor: theme.colors.toggleActive,
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  badgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
    fontFamily: Fonts.ui,
  },
  emptyCard: {
    backgroundColor: theme.colors.surface2,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 6,
      },
      default: {
        borderRadius: 10,
      },
    }),
    padding: 20,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderStyle: 'dashed',
  },
  emptyText: {
    fontSize: 14,
    fontStyle: 'italic',
    color: theme.colors.textMuted,
    fontFamily: Fonts.reading,
    textAlign: 'center',
  },
  seeMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    marginTop: 8,
    backgroundColor: theme.colors.surface2,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 6,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 4,
      },
      default: {
        borderRadius: 8,
      },
    }),
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  seeMoreText: {
    fontSize: 14,
    color: theme.colors.textLight,
    fontFamily: Fonts.reading,
    fontWeight: '500',
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.borderLight,
    marginVertical: 20,
  },
  tabsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.objectBorder,
    gap: 8,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabActive: {
    backgroundColor: theme.colors.toggleActive,
  },
  tabText: {
    fontSize: 12,
    fontWeight: '500',
    color: theme.colors.textMuted,
    fontFamily: Fonts.ui,
  },
  tabTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  tabWithBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  tabBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    fontFamily: Fonts.ui,
  },
  backgroundImage: {
    opacity: 1.0, // 100% visible
    width: '100%',
    height: '100%',
  },
});

export default PlayDrawer;
